import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import api from '../../api/axios';

const API_URL = process.env.REACT_APP_API_URL;

const Analytics = () => {
  const [loading, setLoading] = useState(true);
  const [activitiesLoading, setActivitiesLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30');
  const [recentActivities, setRecentActivities] = useState([]);
  const [stats, setStats] = useState({
    projects: {
      total: 0,
      active: 0,
      completed: 0,
      planning: 0,
      onHold: 0
    },
    financial: {
      totalBilled: 0,
      totalPaid: 0,
      outstanding: 0,
      overdue: 0
    },
    customers: {
      total: 0,
      active: 0,
      newThisMonth: 0
    },
    support: {
      total: 0,
      open: 0,
      resolved: 0,
      avgResolutionTime: 0
    },
    materials: {
      totalItems: 0,
      lowStock: 0,
      totalValue: 0
    }
  });

  // Helper function to calculate average resolution time
  const calculateAvgResolutionTime = (tickets) => {
    if (!tickets || tickets.length === 0) return 0;
    
    const resolvedTickets = tickets.filter(t => t.status === 'resolved' && t.resolved_at && t.created_at);
    if (resolvedTickets.length === 0) return 0;
    
    const totalDays = resolvedTickets.reduce((sum, ticket) => {
      const created = new Date(ticket.created_at);
      const resolved = new Date(ticket.resolved_at);
      const diffTime = Math.abs(resolved - created);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return sum + diffDays;
    }, 0);
    
    return Math.round((totalDays / resolvedTickets.length) * 10) / 10; // Round to 1 decimal place
  };

  // Helper function to format time ago
  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInHours = Math.floor((now - time) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    
    const diffInWeeks = Math.floor(diffInDays / 7);
    return `${diffInWeeks} week${diffInWeeks > 1 ? 's' : ''} ago`;
  };

  // Helper function to get activity icon
  const getActivityIcon = (type) => {
    switch (type) {
      case 'project_update':
        return (
          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2-2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        );
      case 'payment_update':
        return (
          <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'customer_registration':
        return (
          <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        );
      default:
        return (
          <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  // Helper function to get activity background color
  const getActivityBgColor = (type) => {
    switch (type) {
      case 'project_update':
        return 'bg-blue-100';
      case 'payment_update':
        return 'bg-green-100';
      case 'customer_registration':
        return 'bg-purple-100';
      default:
        return 'bg-gray-100';
    }
  };

  // Fetch analytics data from API
  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setActivitiesLoading(true);
      const token = localStorage.getItem('token');
      
      // Fetch projects
      const projectsResponse = await fetch(`${API_URL}/admin/projects`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Fetch customers
      const customersResponse = await fetch(`${API_URL}/admin/customers`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Fetch recent activities
      const activitiesResponse = await fetch(`${API_URL}/admin/dashboard/activities`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (projectsResponse.ok && customersResponse.ok) {
        const projectsData = await projectsResponse.json();
        const customersData = await customersResponse.json();
        
        // Calculate project stats
        const projects = projectsData.data || [];
        const projectStats = {
          total: projects.length,
          active: projects.filter(p => p.status === 'in-progress').length,
          completed: projects.filter(p => p.status === 'completed').length,
          planning: projects.filter(p => p.status === 'planning').length,
          onHold: projects.filter(p => p.status === 'on-hold').length
        };

        // Calculate customer stats
        const customers = customersData.data || [];
        const customerStats = {
          total: customers.length,
          active: customers.filter(c => c.role === 'customer').length,
          newThisMonth: customers.filter(c => {
            const createdDate = new Date(c.created_at);
            const thirtyDaysAgo = new Date();
            thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
            return createdDate > thirtyDaysAgo;
          }).length
        };

        // Fetch real financial data from admin payments table
        const paymentsResponse = await fetch(`${API_URL}/admin/payments`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        let financialStats = {
          totalBilled: 0,
          totalPaid: 0,
          outstanding: 0,
          overdue: 0
        };
        
        if (paymentsResponse.ok) {
          const paymentsData = await paymentsResponse.json();
          const payments = paymentsData.data || [];
          
          console.log('🔍 Analytics - Payments data:', payments);
          console.log('🔍 Analytics - Number of payments:', payments.length);
          console.log('🔍 Analytics - Payment statuses:', payments.map(p => p.status));
          
          financialStats = {
            totalBilled: payments.reduce((sum, p) => sum + (p.amount || 0), 0),
            totalPaid: payments.filter(p => p.status === 'completed' || p.status === 'paid').reduce((sum, p) => sum + (p.amount || 0), 0),
            outstanding: payments.filter(p => p.status === 'pending' || p.status === 'due' || p.status === 'requested').reduce((sum, p) => sum + (p.amount || 0), 0),
            overdue: payments.filter(p => p.status === 'overdue').reduce((sum, p) => sum + (p.amount || 0), 0)
          };
          
          console.log('🔍 Analytics - Financial stats:', financialStats);
        } else {
          console.warn('Failed to fetch payments:', paymentsResponse.status);
        }

        // Fetch real support data from support table
        const { data: supportTickets } = await api.get('/support');
        const avgResolutionTime = calculateAvgResolutionTime(supportTickets?.data || []);
        const supportStats = {
          total: supportTickets?.data?.length || 0,
          open: supportTickets?.data?.filter(t => t.status === 'open').length || 0,
          resolved: supportTickets?.data?.filter(t => t.status === 'resolved').length || 0,
          avgResolutionTime: avgResolutionTime
        };

        // Fetch real materials data from materials table
        const { data: materials } = await api.get('/materials');
        const materialsStats = {
          totalItems: materials?.data?.length || 0,
          lowStock: materials?.data?.filter(m => m.stock < 10).length || 0,
          totalValue: materials?.data?.reduce((sum, m) => sum + (m.price * (m.stock || 0)), 0) || 0
        };

        // Set recent activities if available
        if (activitiesResponse.ok) {
          const activitiesData = await activitiesResponse.json();
          setRecentActivities(activitiesData.data || []);
        } else {
          console.warn('Failed to fetch recent activities:', activitiesResponse.status);
          setRecentActivities([]);
        }

        setStats({
          projects: projectStats,
          financial: financialStats,
          customers: customerStats,
          support: supportStats,
          materials: materialsStats
        });
      } else {
        console.warn('Failed to fetch projects or customers:', projectsResponse.status, customersResponse.status);
        setRecentActivities([]);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics data');
      setRecentActivities([]);
    } finally {
      setLoading(false);
      setActivitiesLoading(false);
    }
  };

  // Export data to CSV
  const exportToCSV = (data, filename) => {
    const csvContent = "data:text/csv;charset=utf-8," + data;
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Generate project report
  const generateProjectReport = () => {
    const reportData = [
      ['Metric', 'Count'],
      ['Total Projects', stats.projects.total],
      ['Active Projects', stats.projects.active],
      ['Completed Projects', stats.projects.completed],
      ['Planning Projects', stats.projects.planning],
      ['On Hold Projects', stats.projects.onHold]
    ].map(row => row.join(',')).join('\n');
    
    exportToCSV(reportData, `project-report-${new Date().toISOString().split('T')[0]}.csv`);
    toast.success('Project report exported successfully');
  };

  // Generate financial report
  const generateFinancialReport = () => {
    const reportData = [
      ['Metric', 'Amount (INR)'],
      ['Total Billed', stats.financial.totalBilled.toLocaleString()],
      ['Total Paid', stats.financial.totalPaid.toLocaleString()],
      ['Outstanding', stats.financial.outstanding.toLocaleString()],
      ['Overdue', stats.financial.overdue.toLocaleString()]
    ].map(row => row.join(',')).join('\n');
    
    exportToCSV(reportData, `financial-report-${new Date().toISOString().split('T')[0]}.csv`);
    toast.success('Financial report exported successfully');
  };

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-display-bold text-gray-900">Analytics & Reports</h1>
            <p className="text-gray-600 mt-1">View detailed reports and analytics.</p>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl p-6 shadow-soft border border-gray-100">
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="h-24 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display-bold text-gray-900">Analytics & Reports</h1>
          <p className="text-gray-600 mt-1">View detailed reports and analytics.</p>
        </div>
        <div className="flex items-center space-x-3">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent"
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="365">Last year</option>
          </select>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Project Stats */}
        <div className="bg-white rounded-2xl p-6 shadow-soft border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2-2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <span className="text-sm font-body-medium text-gray-500">Projects</span>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Total</span>
              <span className="text-lg font-body-semibold text-gray-900">{stats.projects.total}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Active</span>
              <span className="text-sm font-body-semibold text-blue-600">{stats.projects.active}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Completed</span>
              <span className="text-sm font-body-semibold text-green-600">{stats.projects.completed}</span>
            </div>
          </div>
        </div>

        {/* Financial Stats */}
        <div className="bg-white rounded-2xl p-6 shadow-soft border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
            </div>
            <span className="text-sm font-body-medium text-gray-500">Revenue</span>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Total Billed</span>
              <span className="text-lg font-body-semibold text-gray-900">₹{(stats.financial.totalBilled / 100000).toFixed(1)}L</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Paid</span>
              <span className="text-sm font-body-semibold text-green-600">₹{(stats.financial.totalPaid / 100000).toFixed(1)}L</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Outstanding</span>
              <span className="text-sm font-body-semibold text-red-600">₹{(stats.financial.outstanding / 100000).toFixed(1)}L</span>
            </div>
          </div>
        </div>

        {/* Customer Stats */}
        <div className="bg-white rounded-2xl p-6 shadow-soft border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
              </svg>
            </div>
            <span className="text-sm font-body-medium text-gray-500">Customers</span>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Total</span>
              <span className="text-lg font-body-semibold text-gray-900">{stats.customers.total}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Active</span>
              <span className="text-sm font-body-semibold text-purple-600">{stats.customers.active}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">New (30d)</span>
              <span className="text-sm font-body-semibold text-green-600">{stats.customers.newThisMonth}</span>
            </div>
          </div>
        </div>

        {/* Support Stats */}
        <div className="bg-white rounded-2xl p-6 shadow-soft border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M12 2.25a9.75 9.75 0 100 19.5 9.75 9.75 0 000-19.5z" />
              </svg>
            </div>
            <span className="text-sm font-body-medium text-gray-500">Support</span>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Total Tickets</span>
              <span className="text-lg font-body-semibold text-gray-900">{stats.support.total}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Open</span>
              <span className="text-sm font-body-semibold text-orange-600">{stats.support.open}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Avg Resolution</span>
              <span className="text-sm font-body-semibold text-blue-600">{stats.support.avgResolutionTime}d</span>
            </div>
          </div>
        </div>
      </div>

      {/* Charts and Detailed Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Project Status Chart */}
        <div className="bg-white rounded-2xl p-6 shadow-soft border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-body-semibold text-gray-900">Project Status Distribution</h3>
            <button
              onClick={generateProjectReport}
              className="px-3 py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Export
            </button>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-4 h-4 bg-blue-500 rounded"></div>
                <span className="text-sm text-gray-600">Active</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-24 bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-500 h-2 rounded-full" 
                    style={{ width: `${(stats.projects.active / stats.projects.total) * 100}%` }}
                  ></div>
                </div>
                <span className="text-sm font-body-semibold text-gray-900">{stats.projects.active}</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-4 h-4 bg-green-500 rounded"></div>
                <span className="text-sm text-gray-600">Completed</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-24 bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-green-500 h-2 rounded-full" 
                    style={{ width: `${(stats.projects.completed / stats.projects.total) * 100}%` }}
                  ></div>
                </div>
                <span className="text-sm font-body-semibold text-gray-900">{stats.projects.completed}</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-4 h-4 bg-yellow-500 rounded"></div>
                <span className="text-sm text-gray-600">Planning</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-24 bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-yellow-500 h-2 rounded-full" 
                    style={{ width: `${(stats.projects.planning / stats.projects.total) * 100}%` }}
                  ></div>
                </div>
                <span className="text-sm font-body-semibold text-gray-900">{stats.projects.planning}</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-4 h-4 bg-red-500 rounded"></div>
                <span className="text-sm text-gray-600">On Hold</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-24 bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-red-500 h-2 rounded-full" 
                    style={{ width: `${(stats.projects.onHold / stats.projects.total) * 100}%` }}
                  ></div>
                </div>
                <span className="text-sm font-body-semibold text-gray-900">{stats.projects.onHold}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Financial Overview */}
        <div className="bg-white rounded-2xl p-6 shadow-soft border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-body-semibold text-gray-900">Financial Overview</h3>
            <button
              onClick={generateFinancialReport}
              className="px-3 py-1 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              Export
            </button>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
              <div>
                <p className="text-sm text-green-600 font-body-medium">Total Revenue</p>
                <p className="text-2xl font-body-bold text-green-900">₹{(stats.financial.totalBilled / 100000).toFixed(1)}L</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-600 font-body-medium">Paid</p>
                <p className="text-xl font-body-bold text-blue-900">₹{(stats.financial.totalPaid / 100000).toFixed(1)}L</p>
                <p className="text-xs text-blue-600">
                  {stats.financial.totalBilled > 0 ? ((stats.financial.totalPaid / stats.financial.totalBilled) * 100).toFixed(1) : '0'}% of total
                </p>
              </div>
              <div className="p-4 bg-red-50 rounded-lg">
                <p className="text-sm text-red-600 font-body-medium">Outstanding</p>
                <p className="text-xl font-body-bold text-red-900">₹{(stats.financial.outstanding / 100000).toFixed(1)}L</p>
                <p className="text-xs text-red-600">
                  {stats.financial.totalBilled > 0 ? ((stats.financial.outstanding / stats.financial.totalBilled) * 100).toFixed(1) : '0'}% of total
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Additional Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Support Performance */}
        <div className="bg-white rounded-2xl p-6 shadow-soft border border-gray-100">
          <h3 className="text-lg font-body-semibold text-gray-900 mb-4">Support Performance</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Resolution Rate</span>
              <span className="text-sm font-body-semibold text-green-600">
                {stats.support.total > 0 ? ((stats.support.resolved / stats.support.total) * 100).toFixed(1) : '0'}%
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Open Tickets</span>
              <span className="text-sm font-body-semibold text-orange-600">{stats.support.open}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Avg Resolution Time</span>
              <span className="text-sm font-body-semibold text-blue-600">{stats.support.avgResolutionTime} days</span>
            </div>
          </div>
        </div>

        {/* Customer Growth */}
        <div className="bg-white rounded-2xl p-6 shadow-soft border border-gray-100">
          <h3 className="text-lg font-body-semibold text-gray-900 mb-4">Customer Growth</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">New This Month</span>
              <span className="text-sm font-body-semibold text-green-600">{stats.customers.newThisMonth}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Growth Rate</span>
              <span className="text-sm font-body-semibold text-blue-600">
                {stats.customers.total > 0 ? ((stats.customers.newThisMonth / stats.customers.total) * 100).toFixed(1) : '0'}%
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Active Customers</span>
              <span className="text-sm font-body-semibold text-purple-600">{stats.customers.active}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity Feed */}
      <div className="bg-white rounded-2xl p-6 shadow-soft border border-gray-100">
        <h3 className="text-lg font-body-semibold text-gray-900 mb-4">Recent Activity</h3>
        <div className="space-y-4">
          {activitiesLoading ? (
            <div className="animate-pulse space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : recentActivities.length > 0 ? (
            recentActivities.map((activity, index) => (
              <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getActivityBgColor(activity.type)}`}>
                  {getActivityIcon(activity.type)}
                </div>
                <div className="flex-1">
                  {activity.type === 'payment_update' ? (
                    <>
                      <p className="text-sm font-body-medium text-gray-900">
                        <span className="font-semibold">Payment {activity.status}</span>
                        {activity.amount ? ` • ₹${activity.amount}` : ''}
                        {activity.projectName ? ` • Project: ${activity.projectName}` : ''}
                        {activity.phase ? ` • Phase: ${activity.phase}` : ''}
                        {activity.customerName ? ` • Customer: ${activity.customerName}` : ''}
                      </p>
                      <p className="text-xs text-gray-500">{formatTimeAgo(activity.timestamp)}</p>
                    </>
                  ) : activity.type === 'project_update' ? (
                    <>
                      <p className="text-sm font-body-medium text-gray-900">
                        <span className="font-semibold">Project "{activity.projectName}" updated</span>
                        {activity.description ? ` • ${activity.description}` : ''}
                        {activity.customerName ? ` • Customer: ${activity.customerName}` : ''}
                      </p>
                      <p className="text-xs text-gray-500">{formatTimeAgo(activity.timestamp)}</p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm font-body-medium text-gray-900">{activity.title}</p>
                      <p className="text-xs text-gray-500">{formatTimeAgo(activity.timestamp)}</p>
                    </>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8 text-gray-500">
              <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm">No recent activity</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Analytics; 